import React from 'react';
import {
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SNOOZE_OPTIONS } from '../types';

export interface SnoozePopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSnooze: (until: Date) => void;
}

// Map of SNOOZE_OPTIONS indices to translation keys
const SNOOZE_OPTION_KEYS = [
  'screens.operationsInbox.snooze.options.untilTomorrow8am',
  'screens.operationsInbox.snooze.options.untilMonday8am',
  'screens.operationsInbox.snooze.options.for1Hour',
];

/**
 * Popover with snooze options
 */
export function SnoozePopover({ anchorEl, onClose, onSnooze }: SnoozePopoverProps) {
  const { t } = useTranslation();
  const open = Boolean(anchorEl);

  const formatDate = (date: Date): string => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const isToday = date.toDateString() === now.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();

    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    if (isToday) return t('screens.operationsInbox.snooze.dateFormat.todayAt', { time: timeStr });
    if (isTomorrow) return t('screens.operationsInbox.snooze.dateFormat.tomorrowAt', { time: timeStr });

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <List sx={{ minWidth: 200, py: 0.5 }}>
        <ListItem sx={{ py: 0.5, px: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontSize: 10 }}>
            {t('screens.operationsInbox.snooze.title')}
          </Typography>
        </ListItem>
        <Divider />
        {SNOOZE_OPTIONS.map((option, index) => {
          const snoozeDate = option.getDate();
          return (
            <ListItemButton
              key={index}
              onClick={() => {
                onSnooze(snoozeDate);
                onClose();
              }}
              sx={{ py: 1 }}
            >
              <ListItemText
                primary={t(SNOOZE_OPTION_KEYS[index])}
                secondary={formatDate(snoozeDate)}
                primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: 11 }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Popover>
  );
}
